/**
 * Ecranul de autentificare.
 *
 * Se randeaza in locul intregii aplicatii, din `Shell`, cand `GET /api/eu` intoarce 401. Nu e o
 * ruta separata si nu exista redirect catre `/login`, deliberat: URL-ul ramane cel cerut, deci
 * dupa autentificare omul ateriza exact unde voia. Un redirect ar fi pierdut adresa.
 *
 * Al doilea ecran, cel de schimbare obligatorie a parolei, traieste tot aici: o parola pusa de
 * administrator a fost dictata sau trimisa pe un canal pe care nu-l controlam, deci nu are voie sa
 * ramana parola de zi cu zi.
 */

import { useEffect, useRef, useState } from 'react';
import { LUNGIME_MINIMA_PAROLA, politicaParola } from '@evomag/core/parola';
import { esteApiError } from '../../lib/api-errors.ts';
import { useLogin, useSchimbaParola } from '../../hooks/useAuth.ts';

function Cadru({ titlu, subtitlu, children }: { titlu: string; subtitlu: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-page p-4 text-ink-1">
      <main className="tile flex w-full max-w-sm flex-col gap-4 p-6">
        <header className="flex flex-col gap-1">
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="inline-block h-5 w-5 rounded-[var(--radius-sm)]"
              style={{ background: 'var(--bank-bcr)' }}
            />
            <h1 className="text-16">{titlu}</h1>
          </span>
          <p className="text-12 text-ink-2">{subtitlu}</p>
        </header>
        {children}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export function Login() {
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [parola, setParola] = useState('');
  const refEmail = useRef<HTMLInputElement>(null);

  useEffect(() => {
    refEmail.current?.focus();
  }, []);

  const blocat = esteApiError(login.error) && login.error.code === 'limita_depasita';

  return (
    <Cadru titlu="Trezorerie evoMAG" subtitlu="Intră în cont ca să continui.">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim() !== '' && parola !== '') login.mutate({ email, parola });
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-11 text-ink-2">Email</span>
          <input
            ref={refEmail}
            className="input"
            type="email"
            name="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-11 text-ink-2">Parolă</span>
          <input
            className="input"
            type="password"
            name="parola"
            autoComplete="current-password"
            required
            value={parola}
            onChange={(e) => setParola(e.target.value)}
          />
        </label>

        {login.isError ? (
          <p
            className="text-12 text-neg"
            role="alert"
            // `aria-live` ca cititorul de ecran sa anunte esecul fara sa mute focusul din camp.
            aria-live="polite"
          >
            {login.error.message}
          </p>
        ) : null}

        <button
          type="submit"
          className="hit-24 chip justify-center py-2 font-medium text-ink-1"
          disabled={login.isPending || blocat}
        >
          {login.isPending ? 'Se verifică…' : 'Intră în cont'}
        </button>
      </form>

      <p className="text-11 text-ink-3">
        Accesul se acordă din <strong>Setări → Utilizatori</strong>, de către un administrator.
        Dacă nu îți știi parola, cere-i să ți-o reseteze.
      </p>
    </Cadru>
  );
}

// ---------------------------------------------------------------------------
// Schimbarea obligatorie a parolei
// ---------------------------------------------------------------------------

export function SchimbaParolaObligatoriu({ email }: { email: string }) {
  const schimba = useSchimbaParola();
  const [curenta, setCurenta] = useState('');
  const [noua, setNoua] = useState('');
  const [confirmare, setConfirmare] = useState('');

  const politica = noua === '' ? null : politicaParola(noua);
  const potrivesc = confirmare === '' || noua === confirmare;
  const poateTrimite =
    curenta !== '' && noua !== '' && noua === confirmare && politica?.ok === true;

  return (
    <Cadru
      titlu="Alege o parolă nouă"
      subtitlu={`Parola contului ${email} a fost setată de un administrator. Schimb-o cu una pe care o știi doar tu.`}
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (poateTrimite) schimba.mutate({ parolaCurenta: curenta, parolaNoua: noua });
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-11 text-ink-2">Parola primită</span>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={curenta}
            onChange={(e) => setCurenta(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-11 text-ink-2">Parolă nouă</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={noua}
            onChange={(e) => setNoua(e.target.value)}
          />
          {/* Politica se verifica si pe client, cu ACELASI cod ca serverul (`@evomag/core/parola`),
              ca mesajul sa apara la tastare, nu dupa un round-trip. Serverul ramane autoritatea. */}
          <span className={politica && !politica.ok ? 'text-11 text-neg' : 'text-11 text-ink-3'}>
            {politica && !politica.ok
              ? politica.motiv
              : `Minimum ${LUNGIME_MINIMA_PAROLA} caractere.`}
          </span>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-11 text-ink-2">Confirmă parola nouă</span>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            required
            value={confirmare}
            onChange={(e) => setConfirmare(e.target.value)}
          />
          {!potrivesc ? <span className="text-11 text-neg">Cele două nu coincid.</span> : null}
        </label>

        {schimba.isError ? (
          <p className="text-12 text-neg" role="alert">
            {schimba.error.message}
          </p>
        ) : null}

        <button
          type="submit"
          className="hit-24 chip justify-center py-2 font-medium text-ink-1"
          disabled={!poateTrimite || schimba.isPending}
        >
          {schimba.isPending ? 'Se salvează…' : 'Salvează parola'}
        </button>
      </form>

      <p className="text-11 text-ink-3">
        La schimbarea parolei, toate celelalte sesiuni ale contului se închid.
      </p>
    </Cadru>
  );
}
